/**
 * App Controller
 *
 * Central coordination layer that:
 * - Bridges Store and Views
 * - Handles user actions
 * - Manages view lifecycle
 * - Coordinates cross-cutting concerns (history, sync, persistence)
 */

import * as store from "../store/index.js";
import { on, emit, Events } from "../../../engine/core/events.js";
import { escapeHtml } from "../../../engine/core/validation.js";
import * as undoManager from "../../../engine/core/undo-manager.js";
import * as CRDTBridge from "../../../engine/sync/crdt-bridge.js";

// View modules (lazy loaded)
let views = {};

// Current view instance
let activeView = null;
let viewContainer = null;

// ============================================
// Initialization
// ============================================

/**
 * Initialize the controller
 * @param {Object} config - Configuration
 * @param {HTMLElement} config.container - Main view container
 * @param {Object} config.views - View modules { mindmap, kanban, ... }
 */
export function init(config) {
  viewContainer = config.container;
  views = config.views || {};

  // Subscribe to store changes
  store.subscribe(handleStoreChange);

  console.log("[Controller] Initialized");
}

/**
 * Handle store state changes
 * @param {Object} state - New state
 * @param {Object} prevState - Previous state
 * @param {string} action - Action that caused change
 */
function handleStoreChange(state, prevState, action) {
  // Re-render active view if data changed
  if (activeView && views[state.currentView]) {
    renderView(state.currentView);
  }
}

// ============================================
// View Management
// ============================================

/**
 * Switch to a view
 * @param {string} viewName - View to switch to
 */
export function switchView(viewName) {
  // Cleanup previous view
  if (activeView && activeView.destroy) {
    activeView.destroy();
  }

  // Update store
  store.setView(viewName);

  // Render new view
  renderView(viewName);
}

/**
 * Render the current or specified view
 * @param {string} [viewName] - View to render (defaults to current)
 */
export function renderView(viewName = null) {
  const state = store.getState();
  viewName = viewName || state.currentView;

  const viewModule = views[viewName];
  if (!viewModule) {
    console.warn(`[Controller] Unknown view: ${viewName}`);
    return;
  }

  activeView = viewModule;

  // Get view container element
  const container = getViewContainer(viewName);
  if (!container) {
    console.warn(`[Controller] Container not found for view: ${viewName}`);
    return;
  }

  // Create callbacks for view
  const callbacks = createViewCallbacks();

  // Render view with full state (views can pick what they need)
  viewModule.render(
    container,
    {
      data: state.data,
      focusId: state.focusId,
      selectedId: state.selectedId,
      hideCompleted: state.hideCompleted,
      docId: state.currentDocId,
      isShared: state.isShared || false,
      shareRole: state.shareRole,
      ganttScale: state.ganttScale,
      ganttZoom: state.ganttZoom,
      userSetProjectDates: state.userSetProjectDates,
    },
    callbacks,
  );
}

/**
 * Get the container element for a view
 * @param {string} viewName - View name
 * @returns {HTMLElement|null}
 */
function getViewContainer(viewName) {
  // Try specific view container
  const viewEl = document.getElementById(`${viewName}-view`);
  if (viewEl) {
    return viewEl.querySelector(".view-content") || viewEl;
  }

  // Fall back to main container
  return viewContainer;
}

/**
 * Create callbacks object for views
 * @returns {Object} Callbacks
 */
function createViewCallbacks() {
  return {
    // Node operations
    onNodeSelect: (nodeId) => {
      store.setSelected(nodeId);
    },

    onNodeEdit: (nodeId) => {
      // Emit event to open modal
      emit(Events.MODAL_OPEN, { modalId: "task-modal", nodeId });
      store.setModalOpen(true, nodeId);
    },

    onNodeUpdate: (nodeId, updates) => {
      CRDTBridge.updateNode(nodeId, updates);
    },

    onNodeDelete: (nodeId) => {
      // Handle children - move to grandparent
      const node = store.find(nodeId);
      const parent = store.findParent(nodeId);

      if (node && parent && node.children?.length > 0) {
        const grandParent = store.findParent(parent.id);
        if (grandParent) {
          // Move children up before deleting
          node.children.forEach((child) => {
            CRDTBridge.moveNode(child.id, grandParent.id, -1);
          });
        }
      }

      CRDTBridge.removeNode(nodeId);
    },

    onNodeMove: (nodeId, newParentId, index) => {
      CRDTBridge.moveNode(nodeId, newParentId, index);
    },

    onNodeAdd: (parentId) => {
      emit(Events.MODAL_OPEN, { modalId: "task-modal", parentId });
      store.setModalOpen(true, null);
    },

    onNavigate: (nodeId) => {
      store.setFocus(nodeId);
    },

    // Data access (read-only)
    find: store.find,
    findParent: store.findParent,

    // Utility
    getEffectiveDates: getEffectiveDates,
    escapeHtml: escapeHtml,
  };
}

// ============================================
// Utility Functions
// ============================================

/**
 * Calculate effective dates for a node (from children if parent)
 * @param {Object} node - Node
 * @returns {Object} { startDate, endDate }
 */
function getEffectiveDates(node) {
  if (!node) return { startDate: null, endDate: null };

  // If node has explicit dates, use them
  if (node.startDate && node.endDate) {
    return { startDate: node.startDate, endDate: node.endDate };
  }

  // If no children, return node's dates (may be null)
  if (!node.children || node.children.length === 0) {
    return { startDate: node.startDate, endDate: node.endDate };
  }

  // Calculate from children
  let minStart = null;
  let maxEnd = null;

  function collectDates(n) {
    if (n.startDate) {
      if (!minStart || n.startDate < minStart) {
        minStart = n.startDate;
      }
    }
    if (n.endDate) {
      if (!maxEnd || n.endDate > maxEnd) {
        maxEnd = n.endDate;
      }
    }
    (n.children || []).forEach(collectDates);
  }

  node.children.forEach(collectDates);

  return {
    startDate: minStart || node.startDate,
    endDate: maxEnd || node.endDate,
  };
}

// escapeHtml comes from engine/core/validation.js (imported at top).

// ============================================
// Navigation
// ============================================

/**
 * Navigate to a node (zoom/focus)
 * @param {string} nodeId - Node ID
 */
export function navigateTo(nodeId) {
  store.setFocus(nodeId);
  renderView();
}

/**
 * Navigate up to parent
 */
export function navigateUp() {
  if (store.navigateUp()) {
    renderView();
  }
}

/**
 * Select a node
 * @param {string} nodeId - Node ID
 */
export function select(nodeId) {
  store.setSelected(nodeId);
}

// ============================================
// Actions
// ============================================

/**
 * Add a new node
 * @param {string} parentId - Parent node ID
 * @param {Object} nodeData - Node data
 * @returns {Object} Created node
 */
export function addNode(parentId, nodeData) {
  const node = CRDTBridge.addNode(parentId, nodeData);
  renderView();
  return node;
}

/**
 * Update a node
 * @param {string} nodeId - Node ID
 * @param {Object} updates - Updates to apply
 */
export function updateNode(nodeId, updates) {
  CRDTBridge.updateNode(nodeId, updates);
  renderView();
}

/**
 * Delete a node
 * @param {string} nodeId - Node ID
 */
export function deleteNode(nodeId) {
  CRDTBridge.removeNode(nodeId);
  renderView();
}

/**
 * Move a node
 * @param {string} nodeId - Node ID
 * @param {string} newParentId - New parent ID
 * @param {number} index - Position
 */
export function moveNode(nodeId, newParentId, index) {
  CRDTBridge.moveNode(nodeId, newParentId, index);
  renderView();
}

// ============================================
// History
// ============================================

/**
 * Undo last action.
 * Rendering happens automatically: the UndoManager mutates the Y.Map,
 * the CRDT observer rebuilds the Store, subscribers re-render.
 */
export function undo() {
  undoManager.undo();
}

/**
 * Redo last undone action.
 */
export function redo() {
  undoManager.redo();
}
